import { NormalizedExpression } from '@spotify/plugin-confidence-flags-react';

const MAX_LINE_LENGTH = 80;

type FormatContext = {
  indent: string;
  depth: number;
  parentOperator?: 'and' | 'or';
};

const escapeString = (value: string): string => value.replace(/'/g, "''");

const valueToSql = (value: NormalizedExpression.Value): string => {
  if (value === null || value === undefined) {
    return 'NULL';
  }
  if (typeof value === 'string') {
    return `'${escapeString(value)}'`;
  }
  if (typeof value === 'number') {
    return String(value);
  }
  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE';
  }
  if (typeof value === 'object' && 'version' in value) {
    return `'${escapeString(value.version)}'`;
  }
  return 'NULL';
};

const binaryOperatorToSql = (
  operator: NormalizedExpression.BinaryOperator,
): string => {
  switch (operator) {
    case '=':
    case '!=':
    case '>':
    case '>=':
    case '<':
    case '<=':
      return operator;
    default:
      return '=';
  }
};

const getIndent = (ctx: FormatContext): string => {
  if (!ctx.indent) return '';
  return ctx.indent.repeat(ctx.depth);
};

const binaryExpressionToSql = (
  expr: NormalizedExpression.BinaryExpression,
): string => {
  const operator = binaryOperatorToSql(expr.operator);
  const value = valueToSql(expr.value);
  return `${expr.attributeName} ${operator} ${value}`;
};

const unaryExpressionToSql = (
  expr: NormalizedExpression.UnaryExpression,
): string => {
  if (expr.operator === 'is-null') {
    return `${expr.attributeName} IS NULL`;
  }
  return `${expr.attributeName} IS NOT NULL`;
};

const setExpressionToSql = (
  expr: NormalizedExpression.SetExpression,
  ctx: FormatContext,
): string => {
  const sqlOperator = expr.operator === 'any-of' ? 'IN' : 'NOT IN';
  const values = expr.values.map((v: NormalizedExpression.Value) =>
    valueToSql(v),
  );

  // Try inline first
  const inlineResult = `${expr.attributeName} ${sqlOperator} (${values.join(', ')})`;

  // If no indent or fits on one line, return inline
  if (!ctx.indent || inlineResult.length <= MAX_LINE_LENGTH) {
    return inlineResult;
  }

  // Break values across lines
  const innerIndent = getIndent({ ...ctx, depth: ctx.depth + 1 });
  const currentIndent = getIndent(ctx);
  const formattedValues = values
    .map((v, i) => `${innerIndent}${v}${i < values.length - 1 ? ',' : ''}`)
    .join('\n');

  return `${expr.attributeName} ${sqlOperator} (\n${formattedValues}\n${currentIndent})`;
};

const rangeExpressionToSql = (
  expr: NormalizedExpression.RangeExpression,
): string => {
  const { attributeName, operator, range } = expr;
  const startOp = range.startInclusive ? '>=' : '>';
  const endOp = range.endInclusive ? '<=' : '<';
  const startValue = valueToSql(range.start);
  const endValue = valueToSql(range.end);

  const condition = `${attributeName} ${startOp} ${startValue} AND ${attributeName} ${endOp} ${endValue}`;

  if (operator === 'not-between') {
    return `NOT (${condition})`;
  }
  return condition;
};

const logicalExpressionToSql = (
  expr: NormalizedExpression.LogicalExpression,
  ctx: FormatContext,
): string => {
  const sqlOperator = expr.operator === 'and' ? 'AND' : 'OR';
  const needsParens =
    ctx.parentOperator !== undefined && ctx.parentOperator !== expr.operator;

  // Convert operands with updated context
  const childCtx: FormatContext = {
    ...ctx,
    depth: needsParens ? ctx.depth + 1 : ctx.depth,
    parentOperator: expr.operator,
  };

  const operands = expr.operands.map((op: NormalizedExpression.Expression) =>
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    formatExpression(op, childCtx),
  );

  // If no indent, return compact form
  if (!ctx.indent) {
    const joined = operands.join(` ${sqlOperator} `);
    return needsParens ? `(${joined})` : joined;
  }

  // Format with line breaks
  const childIndent = getIndent(childCtx);
  const currentIndent = getIndent(ctx);

  const formattedOperands = operands
    .map((op, i) => {
      if (i === 0) {
        return `${childIndent}${op}`;
      }
      return `${childIndent}${sqlOperator} ${op}`;
    })
    .join('\n');

  if (needsParens) {
    return `(\n${formattedOperands}\n${currentIndent})`;
  }

  return formattedOperands;
};

const formatExpression = (
  expr: NormalizedExpression.Expression,
  ctx: FormatContext,
): string => {
  switch (expr.type) {
    case 'binary':
      return binaryExpressionToSql(expr);
    case 'unary':
      return unaryExpressionToSql(expr);
    case 'set':
      return setExpressionToSql(expr, ctx);
    case 'range':
      return rangeExpressionToSql(expr);
    case 'logical':
      return logicalExpressionToSql(expr, ctx);
    case 'segment':
      return `/* segment: ${expr.segmentId} */`;
    case 'array':
      return `/* array expression on ${expr.attributeName} */`;
    default:
      return '';
  }
};

export const expressionToSql = (
  expr: NormalizedExpression.Expression,
  indent?: number | string,
): string => {
  const indentStr =
    typeof indent === 'number' ? ' '.repeat(indent) : indent || '';

  const ctx: FormatContext = {
    indent: indentStr,
    depth: 0,
    parentOperator: undefined,
  };

  return formatExpression(expr, ctx);
};
